import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FormControlLabel, Grid, Switch, TextField } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  useAddManufacturerMutation,
  useGetManufacturerByIdQuery,
  useUpdateManufacturerMutation,
} from './';
import { Manufacturer } from '../../types';
import {
  logError,
  formatPhoneNumber,
  preventPaste,
  unformatPhoneNumber,
  useImageUpload,
  zipCodeSchema,
  phoneNumberSchema,
  emailSchema,
  optionalPhoneNumberSchema,
  showToast,
  citySchema,
  stateSchema,
  address1Schema,
  address2Schema,
} from '../../utils';
import {
  EntityProfilePage,
  AddressFields,
  EmailField,
  PhoneNumberField,
} from '../../components/form';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { UserRole } from '../../types';
import { selectManageManufacturers } from '../user';

// Define Zod schema for form validation
const manufacturerFormSchema = z.object({
  licenseeName: z
    .string()
    .min(1, 'Licensee name is required')
    .max(250, 'Licensee name is too long'),
  code: z.string().min(1, 'Code is required').max(8, 'Code can be no longer than 8 characters'),
  address1: address1Schema,
  address2: address2Schema,
  city: citySchema,
  state: stateSchema,
  zip: zipCodeSchema,
  phone: phoneNumberSchema,
  fax: optionalPhoneNumberSchema,
  email: emailSchema,
  status: z.boolean(),
});

// Extract type from Zod schema
type ManufacturerFormValues = z.infer<typeof manufacturerFormSchema>;

const ManufacturerProfilePage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  // Get current user's role from Redux store
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const canManageManufacturers = useSelector(selectManageManufacturers);
  const isSupervisorOrCSR =
    (currentUser?.role === UserRole.SUPERVISOR || currentUser?.role === UserRole.CSR) &&
    currentUser?.manufacturerId;

  // Determine the manufacturer ID to use
  // If user is supervisor or CSR, use their manufacturerId; otherwise use the URL param
  const manufacturerId = isSupervisorOrCSR ? currentUser?.manufacturerId?.toString() : id;

  // Redirect supervisor/CSR users without manufacturerId to home page
  useEffect(() => {
    if (
      (currentUser?.role === UserRole.SUPERVISOR || currentUser?.role === UserRole.CSR) &&
      !currentUser?.manufacturerId
    ) {
      navigate('/', { replace: true });
    }
  }, [currentUser?.role, currentUser?.manufacturerId, navigate]);

  // Use the shared image upload hook
  const {
    imageFile: logoFile,
    imagePreview: logoPreview,
    imageChanged: logoChanged,
    handleImageChange: handleLogoChange,
    resetImageState: resetLogoState,
  } = useImageUpload();

  // Get manufacturer data
  const {
    data: manufacturer,
    isLoading,
    error,
    refetch,
  } = useGetManufacturerByIdQuery(manufacturerId ?? '', {
    skip: !manufacturerId,
    refetchOnMountOrArgChange: true,
  });
  const [updateManufacturer, { isLoading: isUpdating }] = useUpdateManufacturerMutation();
  const [addManufacturer, { isLoading: isAdding }] = useAddManufacturerMutation();

  const isInProcess = isUpdating || isAdding;

  // Set up React Hook Form with Zod resolver
  const form = useForm<ManufacturerFormValues>({
    defaultValues: {
      licenseeName: '',
      code: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      fax: '',
      email: '',
      status: true,
    },
    resolver: zodResolver(manufacturerFormSchema),
    mode: 'onTouched',
  });

  const {
    control,
    reset,
    watch,
    formState: { isDirty, isValid },
  } = form;

  // Use a combined dirty state that includes both form changes and logo changes
  const hasChanges = isDirty || logoChanged;

  // Handle form submission with React Hook Form
  const onSubmit = async (data: ManufacturerFormValues) => {
    try {
      // Process phone number to store only digits
      const processedData = {
        ...data,
        phone: unformatPhoneNumber(data.phone),
        fax: data.fax ? unformatPhoneNumber(data.fax) : '',
      };

      if (manufacturerId) {
        await updateManufacturer({
          id: manufacturerId,
          manufacturerData: processedData,
          logoFile: logoChanged ? logoFile || undefined : undefined,
        }).unwrap();
        showToast('Manufacturer successfully updated', 'success');
      } else {
        const newManufacturer: Omit<Manufacturer, 'id'> = {
          licenseeName: processedData.licenseeName,
          code: processedData.code,
          address1: processedData.address1,
          address2: processedData.address2,
          city: processedData.city,
          state: processedData.state,
          zip: processedData.zip,
          phone: processedData.phone,
          fax: processedData.fax,
          email: processedData.email,
          status: processedData.status,
        };

        await addManufacturer({
          manufacturerData: newManufacturer,
          logoFile: logoFile || undefined,
        }).unwrap();
        showToast('Manufacturer successfully created', 'success');
      }

      resetLogoState();
      return Promise.resolve();
    } catch (error) {
      logError('Error updating manufacturer:', error);
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }
  };

  useEffect(() => {
    if (manufacturerId) {
      refetch();
    }
  }, [manufacturerId, refetch]);

  useEffect(() => {
    if (manufacturer) {
      // Reset form with manufacturer data
      reset({
        licenseeName: manufacturer.licenseeName,
        code: manufacturer.code,
        address1: manufacturer.address1,
        address2: manufacturer.address2 ?? '',
        city: manufacturer.city,
        state: manufacturer.state,
        zip: manufacturer.zip,
        phone: manufacturer.phone,
        fax: manufacturer.fax ?? '',
        email: manufacturer.email,
        status: manufacturer.status,
      });
    }
  }, [manufacturer, reset]);

  return (
    <EntityProfilePage
      title="Manufacturer Profile"
      entity={manufacturer}
      isLoading={isLoading}
      error={error}
      isSaving={isInProcess}
      form={form}
      hasChanges={hasChanges}
      isValid={isValid}
      imageName="logo"
      imageUrl={logoPreview ?? manufacturer?.logoUrl}
      imageAlt={watch('licenseeName')}
      uploadButtonText="Upload Logo"
      roundedImage={false}
      isLogo={true}
      isReadOnly={!canManageManufacturers}
      onSubmit={onSubmit}
      onImageChange={handleLogoChange}
    >
      <Grid container spacing={2}>
        {/* Licensee Name */}
        <Grid item xs={12} sm={9}>
          <Controller
            name="licenseeName"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                label="Licensee Name"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                disabled={!canManageManufacturers}
                InputProps={{
                  readOnly: !canManageManufacturers,
                }}
              />
            )}
          />
        </Grid>

        {/* Status toggle */}
        <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
          <Controller
            name="status"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={value}
                    onChange={e => onChange(e.target.checked)}
                    color="primary"
                    disabled={!canManageManufacturers}
                  />
                }
                label={value ? 'Active' : 'Inactive'}
              />
            )}
          />
        </Grid>

        {/* Code */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="code"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                label="Code"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                disabled={!canManageManufacturers}
                InputProps={{
                  readOnly: !canManageManufacturers,
                }}
              />
            )}
          />
        </Grid>

        {/* Address fields */}
        <AddressFields
          control={control}
          address1Name="address1"
          address2Name="address2"
          cityName="city"
          stateName="state"
          zipName="zip"
          isReadOnly={!canManageManufacturers}
        />

        {/* Contact info */}
        <Grid item xs={12} sm={6}>
          <PhoneNumberField
            name="phone"
            control={control}
            formatPhoneNumber={formatPhoneNumber}
            label="Phone Number"
            required
            isReadOnly={!canManageManufacturers}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <PhoneNumberField
            name="fax"
            control={control}
            formatPhoneNumber={formatPhoneNumber}
            label="Fax Number"
            isReadOnly={!canManageManufacturers}
          />
        </Grid>

        {/* Email Fields */}
        <EmailField
          emailName="email"
          control={control}
          handlePaste={preventPaste}
          isReadOnly={!canManageManufacturers}
        />
      </Grid>
    </EntityProfilePage>
  );
};

export default ManufacturerProfilePage;
